import React, { useState, useMemo } from "react";
import {
  Search,
  Grid,
  List,
  Calendar,
  Star,
  Tag,
  Filter,
  X,
  TrendingUp,
  Shield,
  Globe,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { FaRupeeSign } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useFilterContext } from "../../Context/filter_context";
import "./Marketplace.css";

// MUI Imports
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Box,
  InputAdornment,
} from "@mui/material";

const Marketplace = () => {
  const { filtered_products: products } = useFilterContext();
  const [viewMode, setViewMode] = useState("card");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSpeciality, setSelectedSpeciality] = useState("All");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [showFilters, setShowFilters] = useState(true);
  const [externalSearchTerm, setExternalSearchTerm] = useState("");

  // Table sorting state
  const [tableSortField, setTableSortField] = useState(null);
  const [tableSortOrder, setTableSortOrder] = useState("asc"); // 'asc' or 'desc'

  // Extract unique values from actual data
  const categories = useMemo(() => {
    if (!products || products.length === 0) return ["All"];
    const uniqueCategories = new Set();
    products.forEach((domain) => {
      if (domain.category) uniqueCategories.add(domain.category);
      if (domain.categories && Array.isArray(domain.categories)) {
        domain.categories.forEach((cat) => uniqueCategories.add(cat));
      }
    });
    return ["All", ...Array.from(uniqueCategories)].filter(Boolean);
  }, [products]);

  const specialities = useMemo(() => {
    if (!products || products.length === 0) return ["All"];
    const uniqueSpecialities = new Set();
    products.forEach((domain) => {
      if (domain.speciality) uniqueSpecialities.add(domain.speciality);
      if (domain.specialities && Array.isArray(domain.specialities)) {
        domain.specialities.forEach((spec) => uniqueSpecialities.add(spec));
      }
    });
    return ["All", ...Array.from(uniqueSpecialities)].filter(Boolean);
  }, [products]);

  // Filter and sort logic
  const filteredDomains = useMemo(() => {
    if (!products || products.length === 0) return [];

    let filtered = products.filter((domain) => {
      const matchesSearch =
        domain.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        domain.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        domain.whyToBuyExplanation
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const domainCategories = [
        domain.category,
        ...(domain.categories || []),
      ].filter(Boolean);
      const matchesCategory =
        selectedCategory === "All" ||
        domainCategories.includes(selectedCategory);

      const domainSpecialities = [
        domain.speciality,
        ...(domain.specialities || []),
      ].filter(Boolean);
      const matchesSpeciality =
        selectedSpeciality === "All" ||
        domainSpecialities.includes(selectedSpeciality);

      const price = domain.price || 0;
      const matchesMinPrice = !minPrice || price >= parseFloat(minPrice);
      const matchesMaxPrice = !maxPrice || price <= parseFloat(maxPrice);

      const matchesPremium = !premiumOnly || domain.premium === true;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesSpeciality &&
        matchesMinPrice &&
        matchesMaxPrice &&
        matchesPremium
      );
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return (a.price || 0) - (b.price || 0);
        case "price-high":
          return (b.price || 0) - (a.price || 0);
        case "rating":
          return parseFloat(b.rating || 0) - parseFloat(a.rating || 0);
        case "date": {
          const dateA = a.registered_date
            ? new Date(a.registered_date)
            : new Date(0);
          const dateB = b.registered_date
            ? new Date(b.registered_date)
            : new Date(0);
          return dateB - dateA;
        }
        default:
          return (a.name || "").localeCompare(b.name || "");
      }
    });

    return filtered;
  }, [
    products,
    searchTerm,
    selectedCategory,
    selectedSpeciality,
    minPrice,
    maxPrice,
    premiumOnly,
    sortBy,
  ]);

  // Handle table column sorting
  const handleTableSort = (field) => {
    if (tableSortField === field) {
      // Toggle sort order if same field
      setTableSortOrder(tableSortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setTableSortField(field);
      setTableSortOrder("asc");
    }
  };

  // Sort table data based on table sort state
  const sortedTableDomains = useMemo(() => {
    if (!tableSortField || viewMode !== "table") return filteredDomains;

    const sorted = [...filteredDomains].sort((a, b) => {
      let aValue, bValue;

      switch (tableSortField) {
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "category":
          aValue = (a.category || "").toLowerCase();
          bValue = (b.category || "").toLowerCase();
          break;
        case "price":
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case "rating":
          aValue = parseFloat(a.rating || 0);
          bValue = parseFloat(b.rating || 0);
          break;
        case "registrar":
          aValue = (a.registrar || "").toLowerCase();
          bValue = (b.registrar || "").toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return tableSortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return tableSortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredDomains, tableSortField, tableSortOrder, viewMode]);

  // Use sortedTableDomains for table view, filteredDomains for card view
  const displayDomains =
    viewMode === "table" ? sortedTableDomains : filteredDomains;

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("All");
    setSelectedSpeciality("All");
    setMinPrice("");
    setMaxPrice("");
    setPremiumOnly(false);
    setSortBy("name");
  };

  const handleExternalSearch = (e) => {
    e.preventDefault();
    if (externalSearchTerm.trim()) {
      const url = `https://getbrand.gold/cart.php?a=add&domain=register&sld=${encodeURIComponent(externalSearchTerm.trim())}&submit=Search`;
      window.open(url, '_blank');
    }
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedCategory !== "All") count++;
    if (selectedSpeciality !== "All") count++;
    if (minPrice || maxPrice) count++;
    if (premiumOnly) count++;
    return count;
  }, [
    searchTerm,
    selectedCategory,
    selectedSpeciality,
    minPrice,
    maxPrice,
    premiumOnly,
  ]);

  // Sortable table header component
  const SortableHeader = ({ field, label }) => {
    const isActive = tableSortField === field;
    const isAsc = tableSortOrder === "asc";

    return (
      <th
        className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-[#fcdb6a]/20 transition-colors select-none"
        onClick={() => handleTableSort(field)}
      >
        <div className="flex items-center gap-2">
          <span>{label}</span>
          {isActive ? (
            isAsc ? (
              <ArrowUp className="w-4 h-4 text-[#b99653]" />
            ) : (
              <ArrowDown className="w-4 h-4 text-[#b99653]" />
            )
          ) : (
            <ArrowUpDown className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-blue-100">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-gold-dark-gradient hidden lg:block">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold gold-main bg-clip-text text-transparent">
                Domain Marketplace
              </h1>
              <p className="text-gray-600 mt-1">
                Discover premium domains to elevate your business
              </p>
            </div>
          </div>
          <div className="flex gap-6 mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <div className="bg-gold-gradient p-2 rounded-lg">
                <Shield className="w-5 h-5 gold-main" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {products?.length || 0}
                </div>
                <div className="text-xs text-gray-500">Total Domains</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-gold-gradient gold-main p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 gold-main" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {filteredDomains.length}
                </div>
                <div className="text-xs text-gray-500">Available Now</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and View Toggle */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-blue-100">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full">
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by domain name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="text-gray-400 w-5 h-5" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "&:hover fieldset": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#b99653",
                    },
                  },
                }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center white-btn !border-[#b99653] gap-2 font-medium relative"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? "Hide" : "Show"} Filters
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setViewMode("card")}
                className={`p-3 rounded-xl transition-all ${
                  viewMode === "card"
                    ? "bg-gold-dark-gradient text-white shadow-lg"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-3 rounded-xl transition-all ${
                  viewMode === "table"
                    ? "bg-gold-dark-gradient text-white shadow-lg"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-blue-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                Advanced Filters
              </h2>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium px-4 py-2 rounded-lg hover:bg-blue-50 transition-all"
                >
                  <X className="w-4 h-4" />
                  Clear All ({activeFiltersCount})
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Category */}
              <FormControl fullWidth size="small">
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  sx={{
                    borderRadius: "12px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                  }}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Speciality */}
              <FormControl fullWidth size="small">
                <InputLabel id="speciality-label">Speciality</InputLabel>
                <Select
                  labelId="speciality-label"
                  value={selectedSpeciality}
                  label="Speciality"
                  onChange={(e) => setSelectedSpeciality(e.target.value)}
                  sx={{
                    borderRadius: "12px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                  }}
                >
                  {specialities.map((spec) => (
                    <MenuItem key={spec} value={spec}>
                      {spec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sort By */}
              <FormControl fullWidth size="small">
                <InputLabel id="sort-label">Sort By</InputLabel>
                <Select
                  labelId="sort-label"
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{
                    borderRadius: "12px",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b99653",
                    },
                  }}
                >
                  <MenuItem value="name">Name (A-Z)</MenuItem>
                  <MenuItem value="price-low">Price (Low to High)</MenuItem>
                  <MenuItem value="price-high">Price (High to Low)</MenuItem>
                  <MenuItem value="rating">Highest Rated</MenuItem>
                  <MenuItem value="date">Newest First</MenuItem>
                </Select>
              </FormControl>

              {/* Min Price */}
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Min Price (₹)"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="0"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaRupeeSign className="text-gray-400 w-4 h-4" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "&:hover fieldset": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#b99653",
                    },
                  },
                }}
              />

              {/* Max Price */}
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max Price (₹)"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Any"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaRupeeSign className="text-gray-400 w-4 h-4" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "&:hover fieldset": {
                      borderColor: "#b99653",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#b99653",
                    },
                  },
                }}
              />
            </div>

            {/* Premium Checkbox */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={premiumOnly}
                    onChange={(e) => setPremiumOnly(e.target.checked)}
                    sx={{
                      color: "#b99653",
                      "&.Mui-checked": {
                        color: "#b99653",
                      },
                    }}
                  />
                }
                label={
                  <span className="text-sm font-medium text-gray-700">
                    Show Premium Domains Only
                  </span>
                }
              />
            </div>

            {/* Active Filters Display */}
            {activeFiltersCount > 0 && (
              <Box className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm font-semibold text-gray-700">
                    Active Filters:
                  </span>
                  {searchTerm && (
                    <Chip
                      label={`Search: "${searchTerm}"`}
                      onDelete={() => setSearchTerm("")}
                      size="small"
                      sx={{
                        backgroundColor: "#fcdb6a",
                        "& .MuiChip-deleteIcon": {
                          color: "#101c4c",
                        },
                      }}
                    />
                  )}
                  {selectedCategory !== "All" && (
                    <Chip
                      label={`Category: ${selectedCategory}`}
                      onDelete={() => setSelectedCategory("All")}
                      size="small"
                      sx={{
                        backgroundColor: "#fcdb6a",
                        "& .MuiChip-deleteIcon": {
                          color: "#101c4c",
                        },
                      }}
                    />
                  )}
                  {selectedSpeciality !== "All" && (
                    <Chip
                      label={`Speciality: ${selectedSpeciality}`}
                      onDelete={() => setSelectedSpeciality("All")}
                      size="small"
                      sx={{
                        backgroundColor: "#fcdb6a",
                        "& .MuiChip-deleteIcon": {
                          color: "#101c4c",
                        },
                      }}
                    />
                  )}
                  {(minPrice || maxPrice) && (
                    <Chip
                      label={`Price: ₹${minPrice || "0"} - ₹${maxPrice || "∞"}`}
                      onDelete={() => {
                        setMinPrice("");
                        setMaxPrice("");
                      }}
                      size="small"
                      sx={{
                        backgroundColor: "#fcdb6a",
                        "& .MuiChip-deleteIcon": {
                          color: "#101c4c",
                        },
                      }}
                    />
                  )}
                  {premiumOnly && (
                    <Chip
                      label="Premium Only"
                      onDelete={() => setPremiumOnly(false)}
                      size="small"
                      sx={{
                        backgroundColor: "#fcdb6a",
                        "& .MuiChip-deleteIcon": {
                          color: "#101c4c",
                        },
                      }}
                    />
                  )}
                </div>
              </Box>
            )}
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-gray-600 font-medium">
            Showing{" "}
            <span className="text-blue-600 font-bold">
              {displayDomains.length}
            </span>{" "}
            of <span className="font-bold">{products?.length || 0}</span>{" "}
            domains
          </div>
        </div>

        {/* Card View */}
        {viewMode === "card" && displayDomains.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayDomains.map((domain) => (
              <div
                key={domain._id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-blue-200 group flex flex-col h-full"
              >
                <div className="bg-gold-gradient p-4 relative rounded-t-2xl">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold gold-main break-all capitalize">
                        {domain.name}
                      </h3>
                      {domain.status && (
                        <span className="inline-block mt-2 bg-white/20 text-[#101c4c] text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
                          {domain.status}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {domain.premium && (
                        <span className="bg-[#b99653] text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md uppercase tracking-wide">
                          <Star className="w-3 h-3 text-white" />
                          Premium
                        </span>
                      )}

                      <Link to={`/Marketplace_Product/${domain._id}`}>
                        <button
                          className="relative group w-9 h-9 flex items-center justify-center rounded-full bg-white/70 border border-[#b99653]/50 hover:scale-105 hover:shadow-md transition-all duration-300"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5 text-[#b99653] transition-colors duration-300" />
                          <span className="absolute -bottom-7 text-[10px] text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            View
                          </span>
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-10">
                    {domain.description ||
                      domain.whyToBuyExplanation ||
                      "Premium domain available for purchase"}
                  </p>

                  <div className="space-y-3 mb-4">
                    {domain.category && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Tag className="w-4 h-4 text-[#b99653]" />
                        <span className="font-medium">{domain.category}</span>
                      </div>
                    )}
                    {domain.rating && (
                      <div className="flex items-center gap-2 text-sm">
                        <Star className="w-4 h-4 text-[#fcdb6a] fill-current" />
                        <span className="font-semibold text-[#101c4c]">
                          {domain.rating}
                        </span>
                        <span className="text-gray-500">/ 5.0</span>
                      </div>
                    )}
                    {domain.registered_date && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-[#b99653]" />
                        <span>
                          {new Date(
                            domain.registered_date
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {domain.registrar && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Shield className="w-4 h-4 text-[#b99653]" />
                        <span>{domain.registrar}</span>
                      </div>
                    )}
                  </div>

                  {(domain.specialities?.length > 0 || domain.speciality) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {domain.speciality && (
                        <span className="bg-[#fcdb6a]/30 text-[#101c4c] text-xs font-semibold px-3 py-1 rounded-full">
                          {domain.speciality}
                        </span>
                      )}
                      {domain.specialities?.map((spec, idx) => (
                        <span
                          key={idx}
                          className="bg-[#fcdb6a]/30 text-[#101c4c] text-xs font-semibold px-3 py-1 rounded-full"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-auto pt-4 border-t border-[#b99653]/20 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <FaRupeeSign className="w-6 h-6 text-[#b99653]" />
                      <span className="text-3xl font-bold text-[#101c4c]">
                        {(domain.price || 0).toLocaleString()}
                      </span>
                    </div>
                    <Link to={`/Marketplace-checkout/${domain._id}`}>
                      <button className="white-btn !border-[#b99653]">
                        Buy Now
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table View */}
        {viewMode === "table" && displayDomains.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-blue-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gold-gradient border-b-2 border-blue-200">
                  <tr className="group">
                    <SortableHeader field="name" label="Domain Name" />
                    <SortableHeader field="category" label="Category" />
                    <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-40">
                      Speciality
                    </th>
                    <SortableHeader field="price" label="Price" />
                    <SortableHeader field="rating" label="Rating" />
                    <SortableHeader field="registrar" label="Registrar" />
                    <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[180px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {displayDomains.map((domain) => (
                    <tr
                      key={domain._id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap align-middle">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">
                            {domain.name}
                          </span>
                          {domain.premium && (
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Premium
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-middle">
                        <span className="text-sm font-medium text-gray-700">
                          {domain.category || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 w-40 align-middle">
                        <div className="flex items-center gap-1 overflow-hidden">
                          {domain.speciality && (
                            <span className="bg-gold-gradient text-[10px] font-semibold px-2 py-1 rounded whitespace-nowrap inline-block">
                              {domain.speciality}
                            </span>
                          )}
                          {domain.specialities?.slice(0, 1).map((spec, idx) => (
                            <span
                              key={idx}
                              className="bg-gold-gradient text-[10px] font-semibold px-2 py-1 rounded whitespace-nowrap inline-block"
                            >
                              {spec}
                            </span>
                          ))}
                          {((domain.speciality &&
                            domain.specialities?.length > 0) ||
                            domain.specialities?.length > 1) && (
                            <span className="bg-[#fcdb6a]/40 text-gray-700 text-[10px] font-semibold px-2 py-1 rounded whitespace-nowrap inline-block">
                              +
                              {(domain.speciality ? 0 : 1) +
                                (domain.specialities?.length || 0) -
                                1}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-middle">
                        <span className="text-xl font-bold">
                          ₹{(domain.price || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-middle">
                        {domain.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span className="text-sm font-semibold text-gray-900">
                              {domain.rating}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-middle">
                        <span className="text-sm text-gray-600">
                          {domain.registrar || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap align-middle min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <Link to={`/Marketplace_Product/${domain._id}`}>
                            <button
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/70 border border-[#b99653]/50 hover:bg-[#b99653] hover:scale-105 hover:shadow-md transition-all duration-300 flex-shrink-0"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4 text-[#b99653] hover:text-white transition-colors duration-300" />
                            </button>
                          </Link>

                          <Link to={`/Marketplace-checkout/${domain._id}`}>
                            <button className="white-btn !border-[#b99653] !py-1.5 !px-3 !text-sm hover:bg-[#b99653] hover:text-white transition-all duration-300 whitespace-nowrap flex-shrink-0">
                              BUY NOW
                            </button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results */}
        {displayDomains.length === 0 && products && products.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-12 border border-blue-100">
            <div className="text-center mb-8">
              <div className="bg-gold-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                No Match Found in Our Marketplace
              </h3>
              <p className="text-gray-500 mb-2">
                No domains available based on your search criteria
              </p>
              <p className="text-gray-400 text-sm">
                Try searching for your domain on our partner platform
              </p>
            </div>

            {/* External Search Bar */}
            <div className="max-w-2xl mx-auto mb-6">
              <form onSubmit={handleExternalSearch}>
                <div className="relative">
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search for your domain (e.g., example)"
                    value={externalSearchTerm}
                    onChange={(e) => setExternalSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className="text-gray-400 w-5 h-5" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <button
                            type="submit"
                            className="bg-gold-dark-gradient text-black px-6 py-2 rounded-lg hover:shadow-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!externalSearchTerm.trim()}
                          >
                            Search
                          </button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        paddingRight: "8px",
                        "&:hover fieldset": {
                          borderColor: "#b99653",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#b99653",
                        },
                      },
                    }}
                  />
                </div>
              </form>
            </div>

            {activeFiltersCount > 0 && (
              <div className="text-center pt-6 border-t border-gray-200">
                <p className="text-gray-500 mb-3 text-sm">
                  Or try adjusting your filters to see more domains
                </p>
                <button
                  onClick={clearFilters}
                  className="bg-gold-gradient text-black px-6 py-3 rounded-xl hover:shadow-lg transition-all font-semibold"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {(!products || products.length === 0) && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-blue-100">
            <div className="animate-spin bg-gold-gradient w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
              <div className="bg-white w-12 h-12 rounded-full"></div>
            </div>
            <p className="text-gray-500 text-lg font-semibold">
              Loading domains...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
